import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { walletAPI, marketsAPI, authAPI, transactionAPI, swapAPI } from '../../utils/api';
import { formatCurrency } from '../../utils/helpers';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import StatusBadge from '../../components/StatusBadge';
import { useTheme } from '../../context/ThemeContext';
import {
  ArrowDown,
  ArrowUp,
  ArrowLeftRight,
  Send,
  Search,
  ChevronDown,
  ArrowUpDown,
  Filter,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { createChart, ColorType, AreaSeries } from 'lightweight-charts';
import { TokenBTC, TokenETH, TokenUSDT, TokenXRP } from '@web3icons/react';

const DashboardNew = () => {
  const [wallets, setWallets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterType, setFilterType] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
    // Compute filtered transactions
    const filteredTransactions = transactions.filter((tx) => {
      const typeMatch = filterType === 'All' || tx.type === filterType;
      const statusMatch = filterStatus === 'All' || (tx.status?.toLowerCase() === filterStatus.toLowerCase());
      return typeMatch && statusMatch;
    });
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartPeriod, setChartPeriod] = useState('month');
  const [selectedChartAsset, setSelectedChartAsset] = useState('BTC');
  const [exchangeMode, setExchangeMode] = useState('buy');
  const [user, setUser] = useState(null);
  const { theme } = useTheme();
  const coinGeckoIdBySymbol = {
    BTC: 'bitcoin',
    ETH: 'ethereum',
    USDT: 'tether',
    XRP: 'ripple',
  };
  const [exchangeData, setExchangeData] = useState({
    from: 'BTC',
    to: 'USDT',
    fromAmount: '0',
    toAmount: '0',
    exchangeRate: null,
  });

  const sanitizeNumericInput = (value) => {
    const cleaned = value.replace(/,/g, '').replace(/[^0-9.]/g, '');
    const [integerPart = '', ...decimalParts] = cleaned.split('.');
    if (decimalParts.length === 0) return integerPart;
    return `${integerPart}.${decimalParts.join('')}`;
  };

  const formatNumberWithCommas = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const [integerPart, decimalPart] = String(value).split('.');
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
  };

  const getWalletBalanceBySymbol = (symbol) => {
    const wallet = wallets.find((w) => w.asset_symbol === symbol);
    return Number(wallet?.available_balance ?? wallet?.balance ?? 0);
  };

  // Get available crypto assets from wallets
  const cryptoAssets = wallets.filter((w) => ['BTC', 'ETH', 'USDT', 'XRP'].includes(w.asset_symbol));
  const toAssetOptions = cryptoAssets.filter(
    (asset) => asset.asset_symbol !== exchangeData.from
  );

  useEffect(() => {
    if (exchangeData.from === exchangeData.to) {
      const fallbackToAsset = cryptoAssets.find(
        (asset) => asset.asset_symbol !== exchangeData.from
      )?.asset_symbol;

      if (fallbackToAsset) {
        setExchangeData((prev) => ({ ...prev, to: fallbackToAsset }));
      }
    }
  }, [cryptoAssets, exchangeData.from, exchangeData.to]);

  // Update fromAmount when wallets or from asset changes
  useEffect(() => {
    const fromBalance = getWalletBalanceBySymbol(exchangeData.from);
    setExchangeData((prev) => ({
      ...prev,
      fromAmount: fromBalance.toString(),
    }));
    // eslint-disable-next-line
  }, [wallets, exchangeData.from]);

  const handleDashboardExchange = async (e) => {
    e.preventDefault();

    const { from, to, fromAmount } = exchangeData;
    const amount = parseFloat(fromAmount);

    if (!from || !to || !fromAmount || Number.isNaN(amount) || amount <= 0) {
      toast.error('Please fill in all fields');
      return;
    }

    if (from === to) {
      toast.error('Please select different assets');
      return;
    }

    const balance = getWalletBalanceBySymbol(from);
    if (amount > balance) {
      toast.error('Insufficient balance');
      return;
    }

    try {
      const response = await swapAPI.request({
        from_asset: from,
        to_asset: to,
        from_amount: amount,
      });

      toast.success(
        `Exchange successful! You'll receive ~${response.data.estimated_amount} ${to}`
      );

      setExchangeData((prev) => ({
        ...prev,
        fromAmount: '0',
        toAmount: '0',
      }));

      await fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Exchange failed');
    }
  };

  const chartContainerRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const downsampleSeries = (series, maxPoints) => {
    if (!Array.isArray(series) || series.length <= maxPoints) return series;
    const sampled = [];
    const step = (series.length - 1) / (maxPoints - 1);
    for (let i = 0; i < maxPoints; i++) {
      sampled.push(series[Math.round(i * step)]);
    }
    return sampled;
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await authAPI.getMe();
        setUser(response.data);
      } catch (e) {
        setUser(null);
      }
    };
    fetchUser();
  }, []);

  // Real-time crypto data from CoinGecko
  const [cryptoData, setCryptoData] = useState([
    {
      name: 'Bitcoin',
      symbol: 'BTC',
      color: '#ffe1c7',
      icon: <TokenBTC size={32} />,
    },
    {
      name: 'Tether',
      symbol: 'USDT',
      color: '#c7e6ff',
      icon: <TokenUSDT size={32} />,
    },
    {
      name: 'Ethereum',
      symbol: 'ETH',
      color: '#c7d3ff',
      icon: <TokenETH size={32} />,
    },
    // {
    //   name: 'Ripple',
    //   symbol: 'XRP',
    //   color: '#e3e3e3',
    //   icon: <TokenXRP size={32} />,
    // },
  ]);

  
  // Fiat balance (USD wallet) and total portfolio value in USD. Recomputes
  // whenever wallets or crypto prices change.
  const { fiatBalance, totalPortfolioUsd } = useMemo(() => {
    const priceBySymbol = {};
    (cryptoData || []).forEach((c) => {
      const p = parseFloat(String(c.price || '').replace(/,/g, ''));
      if (!Number.isNaN(p)) priceBySymbol[c.symbol] = p;
    });
    // Stablecoins peg to 1 USD when no live price is available.
    if (priceBySymbol.USDT == null) priceBySymbol.USDT = 1;
    if (priceBySymbol.USDC == null) priceBySymbol.USDC = 1;

    let fiat = 0;
    let total = 0;
    (wallets || []).forEach((w) => {
      const amount = Number(w.available_balance ?? w.balance ?? 0);
      if (!amount) return;
      if (w.asset_symbol === 'USD') {
        fiat += amount;
        total += amount;
      } else {
        total += amount * (priceBySymbol[w.asset_symbol] || 0);
      }
    });
    return { fiatBalance: fiat, totalPortfolioUsd: total };
  }, [wallets, cryptoData]);

  // Update toAmount and exchangeRate when from/to/fromAmount changes
  useEffect(() => {
    const fromAsset = exchangeData.from;
    const toAsset = exchangeData.to;
    const fromAmount = exchangeData.fromAmount;
    if (fromAsset && toAsset && fromAmount && parseFloat(fromAmount) > 0 && cryptoData.length > 0) {
      const fromCrypto = cryptoData.find((c) => c.symbol === fromAsset);
      const toCrypto = cryptoData.find((c) => c.symbol === toAsset);
      const fromPrice = fromCrypto ? parseFloat((fromCrypto.price + '').replace(/,/g, '')) : 0;
      const toPrice = toCrypto ? parseFloat((toCrypto.price + '').replace(/,/g, '')) : 1;
      if (fromPrice > 0 && toPrice > 0) {
        const rate = fromPrice / toPrice;
        setExchangeData((prev) => ({
          ...prev,
          exchangeRate: rate,
          toAmount: (parseFloat(fromAmount) * rate).toFixed(2),
        }));
      } else {
        setExchangeData((prev) => ({ ...prev, exchangeRate: null, toAmount: '0' }));
      }
    } else {
      setExchangeData((prev) => ({ ...prev, exchangeRate: null, toAmount: '0' }));
    }
    // eslint-disable-next-line
  }, [exchangeData.from, exchangeData.to, exchangeData.fromAmount, cryptoData]);

  // Fetch real-time prices and 24h change from CoinGecko
  useEffect(() => {
    let isMounted = true;
    const fetchCryptoData = async () => {
      try {
        const ids = Object.values(coinGeckoIdBySymbol);
        const res = await fetch(
          `https://api.coingecko.com/api/v3/simple/price?ids=${ids.join(',')}&vs_currencies=usd&include_24hr_change=true`
        );
        const data = await res.json();
        if (!isMounted) return;
        setCryptoData((prev) => prev.map((c) => {
          const id = coinGeckoIdBySymbol[c.symbol];
          return {
            ...c,
            price: data[id]?.usd?.toLocaleString(undefined, { maximumFractionDigits: 2 }) ?? '-',
            change: data[id]?.usd_24h_change?.toFixed(2) ?? '-',
          };
        }));
      } catch (e) {
        // fallback: keep previous data
      }
    };
    fetchCryptoData();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch real transactions from API
  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const res = await transactionAPI.getAll();
        // Map API response to dashboard format
        const txs = (res.data || []).map((tx) => {
          const rawType = tx.type || 'transaction';
          return {
            id: tx.id,
            type: rawType,
            asset_symbol: tx.asset_symbol || tx.currency || '',
            amount: tx.amount ?? 0,
            status: tx.status || 'pending',
            created_at: tx.created_at,
          };
        });
        // Optionally sort by created_at descending
        txs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setTransactions(txs);
      } catch (e) {
        setTransactions([]);
      }
    };
    fetchTransactions();
  }, []);

  // Wallets from API
  // setWallets is already called in fetchDashboardData
  // Map known icons/colors
  const assetIconMap = {
    BTC: <TokenBTC size={24} />,
    ETH: <TokenETH size={24} />,
    USDT: <TokenUSDT size={24} />,
    XRP: <TokenXRP size={24} />,
  };
  const assetColorMap = {
    BTC: '#ffe1c7',
    ETH: '#c7d3ff',
    USDT: '#c7e6ff',
    XRP: '#e3e3e3',
  };

  const assetBadgeConfig = {
    BTC: { icon: '₿', color: '#F7931A', bgClass: 'bg-orange-100' },
    ETH: { icon: 'Ξ', color: '#627EEA', bgClass: 'bg-purple-100' },
    USDT: { icon: '₮', color: '#26A17B', bgClass: 'bg-green-100' },
    USDC: { icon: '$', color: '#2775CA', bgClass: 'bg-blue-100' },
    BNB: { icon: '⟠', color: '#F0B90B', bgClass: 'bg-yellow-100' },
    XRP: { icon: '✕', color: '#23292F', bgClass: 'bg-gray-100' },
    SOL: { icon: '◎', color: '#9945FF', bgClass: 'bg-violet-100' },
    ADA: { icon: '₳', color: '#0033AD', bgClass: 'bg-indigo-100' },
    DOGE: { icon: 'Ð', color: '#C2A633', bgClass: 'bg-amber-100' },
    LTC: { icon: 'Ł', color: '#345D9D', bgClass: 'bg-slate-100' },
    MATIC: { icon: '⬡', color: '#8247E5', bgClass: 'bg-purple-100' },
    USD: { icon: '$', color: '#2E7D32', bgClass: 'bg-green-100' },
  };

  const chartSelectableAssets = Array.from(
    new Set([
      ...cryptoData.map((asset) => asset.symbol),
      ...cryptoAssets.map((asset) => asset.asset_symbol),
    ])
  ).filter((symbol) => coinGeckoIdBySymbol[symbol]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Chart: fetch real price history from CoinGecko
  useEffect(() => {
    let isMounted = true;
    const fetchChartData = async () => {
      if (loading || !chartContainerRef.current) return;

      setChartLoading(true);
      if (chartInstanceRef.current) {
        chartInstanceRef.current.remove();
        chartInstanceRef.current = null;
      }
      const coinId = coinGeckoIdBySymbol[selectedChartAsset] || 'bitcoin';
      // Map chartPeriod to CoinGecko days param
      const periodMap = { day: 1, week: 7, month: 30, year: 365 };
      const days = periodMap[chartPeriod] || 30;

      // Lower timeframes should show more detailed points than higher timeframes.
      const maxPointsMap = {
        day: 288,
        week: 168,
        month: 90,
        year: 52,
      };

      try {
        const url = `https://api.coingecko.com/api/v3/coins/${coinId}/market_chart?vs_currency=usd&days=${days}`;
        const res = await fetch(url);
        const data = await res.json();
        if (!isMounted) return;
        const prices = (data.prices || []).map(([ts, value]) => ({
          time: Math.floor(ts / 1000),
          value,
        }));
        const maxPoints = maxPointsMap[chartPeriod] || 90;
        initializeMainChart(downsampleSeries(prices, maxPoints));
      } catch (e) {
        initializeMainChart(); // fallback to mock
      } finally {
        if (isMounted) {
          setChartLoading(false);
        }
      }
    };
    fetchChartData();
    return () => {
      isMounted = false;
      if (chartInstanceRef.current) {
        chartInstanceRef.current.remove();
        chartInstanceRef.current = null;
      }
    };
  }, [chartPeriod, theme, loading, selectedChartAsset]);

  const fetchDashboardData = async () => {
    try {
      const [walletsRes] = await Promise.all([walletAPI.getAll()]);
      setWallets(walletsRes.data || []);
      // Transactions are now fetched separately
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Initialize chart with real or fallback data
  const initializeMainChart = (chartData) => {
    if (!chartContainerRef.current) return;
    const container = chartContainerRef.current;
    const width = container.clientWidth || 600;
    const isDark = theme === 'dark';
    const chartBackground = isDark ? '#0f172a' : '#f7f8fc';
    const chartText = isDark ? '#9ca3af' : '#7B8591';
    const chartGrid = isDark ? '#1f2937' : '#E5E7EB';
    const chart = createChart(container, {
      layout: {
        background: { type: ColorType.Solid, color: chartBackground },
        textColor: chartText,
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: chartGrid },
        horzLines: { color: chartGrid },
      },
      width: width,
      height: 300,
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
      },
    });
    const areaSeries = chart.addSeries(AreaSeries, {
      lineColor: isDark ? '#21bcfe' : '#062136',
      topColor: isDark ? '#3671FF' : '#062136',
      bottomColor: isDark ? 'rgba(25, 113, 255, 0.05)' : 'rgba(6, 33, 54, 0.08)',
      lineWidth: 1,
    });
    // Use real chart data if available
    if (Array.isArray(chartData) && chartData.length > 0) {
      areaSeries.setData(chartData);
    } else {
      // fallback: generate mock data
      const dataPoints = chartPeriod === 'day' ? 120 : chartPeriod === 'week' ? 84 : chartPeriod === 'month' ? 60 : 24;
      const basePrice = 22000;
      const data = [];
      const now = Math.floor(Date.now() / 1000);
      for (let i = dataPoints - 1; i >= 0; i--) {
        const time = now - i * (chartPeriod === 'day' ? 720 : chartPeriod === 'week' ? 7200 : chartPeriod === 'month' ? 43200 : 1209600);
        const price = basePrice + Math.sin((i / dataPoints) * Math.PI * 2) * 5000 + Math.random() * 2000;
        data.push({ time: time, value: price });
      }
      areaSeries.setData(data);
    }
    chartInstanceRef.current = chart;
    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chartInstanceRef.current) {
        chartInstanceRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
        });
      }
    };
    window.addEventListener('resize', handleResize);
    // Return cleanup function
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  };

  const generateMiniChartData = (symbol, isPositive) => {
    const seedFromSymbol = (symbol || 'DEFAULT')
      .split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const seededRandom = (seed) => {
      const x = Math.sin(seed) * 10000;
      return x - Math.floor(x);
    };

    const points = 20;
    const data = [];
    const base = 50;
    for (let i = 0;i < points;i++) {
      const trend = isPositive ? (i / points) * 30 : -(i / points) * 20;
      const noise = (seededRandom(seedFromSymbol + i * 13.37) - 0.5) * 10;
      data.push(base + trend + noise);
    }
    return data;
  };

  const miniChartDataBySymbol = useMemo(() => {
    const result = {};
    cryptoData.forEach((crypto) => {
      result[crypto.symbol] = generateMiniChartData(
        crypto.symbol,
        Number(crypto.change) > 0
      );
    });
    return result;
  }, [cryptoData]);

  const getTransactionIcon = (type) => {
    const accentColor = theme === 'dark' ? '#3671FF' : '#062136';
    const buyColor = theme === 'dark' ? '#21bcfe' : '#062136';
    switch (type) {
      case 'Deposit':
        return <ArrowDown className="w-5 h-5" style={{ color: accentColor }} />;
      case 'Withdrawal':
        return <ArrowUp className="w-5 h-5 text-[#E04B4B]" />;
      case 'Buy':
        return <ArrowDown className="w-5 h-5" style={{ color: buyColor }} />;
      case 'Sell':
        return <ArrowUp className="w-5 h-5 text-[#E04B4B]" />;
      case 'Trade':
        return <ArrowLeftRight className="w-5 h-5" style={{ color: accentColor }} />;
      default:
        return <ArrowLeftRight className="w-5 h-5" style={{ color: accentColor }} />;
    }
  };

  const MiniAreaChart = ({ data, color, isPositive }) => {
    const canvasRef = useRef(null);

    useEffect(() => {
      if (!canvasRef.current) return;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const height = canvas.height;

      ctx.clearRect(0, 0, width, height);

      if (data.length === 0) return;

      const min = Math.min(...data);
      const max = Math.max(...data);
      const range = max - min || 1;

      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.lineWidth = 2;

      ctx.beginPath();
      const stepX = width / (data.length - 1);

      for (let i = 0;i < data.length;i++) {
        const x = i * stepX;
        const normalizedValue = (data[i] - min) / range;
        const y = height - normalizedValue * height;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }

      // Fill area
      ctx.lineTo(width, height);
      ctx.lineTo(0, height);
      ctx.closePath();

      // Create gradient
      const gradient = ctx.createLinearGradient(0, 0, 0, height);
      gradient.addColorStop(0, color + '90');
      gradient.addColorStop(1, color + '10');
      ctx.fillStyle = gradient;
      ctx.fill();

      // Draw line
      // ctx.stroke();
    }, [data, color]);

    return (
      <canvas ref={canvasRef} width={120} height={40} className="w-full h-10" />
    );
  };

  return (
    <div className="min-h-screen bg-[#F9F9F9] dark:bg-slate-950">
      <div className="max-w-[1920px] mx-auto">
        {loading && (
          <div className="flex items-center justify-center min-h-[40vh]">
            <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .loading-spinner {
            width: 48px;
            height: 48px;
            border: 4px solid #e5e7eb;
            border-top-color: #062136;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
        `}</style>
            <div className="loading-spinner"></div>
          </div>
        )}

        {!loading && (
          <div className="grid grid-cols-1 xl:grid-cols-12 min-h-screen">
            {/* Left Column - Main Content */}
            <div className="col-span-1 xl:col-span-8 space-y-6 p-4 md:p-6 xl:pr-8 xl:pl-10 xl:py-10 border-r-0 xl:border-r border-gray-200 dark:border-slate-800 overflow-x-hidden">
              {/* Header */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-slate-100">
                    Dashboard
                  </h1>
                  <div className="flex items-center space-x-2">
                    <p className="text-base md:text-lg mt-1 text-gray-500 dark:text-slate-400">
                      Welcome Back
                    </p>
                    <p className="text-base md:text-lg mt-1 font-bold text-gray-900 dark:text-slate-100">
                      {user?.full_name?.charAt(0).toUpperCase() +
                        user?.full_name?.slice(1)}
                      !
                    </p>
                  </div>
                </div>
                {/* <div className="relative w-full md:w-auto md:ml-auto justify-center">
                  <input
                    type="text"
                    placeholder="Search Any thing"
                    className="pl-4 pr-10 py-2 rounded-lg bg-gray-100 border border-gray-200 text-gray-900 placeholder:text-gray-400 w-full md:w-[300px] dark:bg-slate-900/60 dark:border-slate-700 dark:text-gray-100 dark:placeholder:text-gray-400"
                  />
                  <Search
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500"
                  />
                </div> */}
              </div>

              {/* Account balance summary — fiat + total portfolio value */}
              <Card
                className="p-5 md:p-6 bg-gradient-to-r from-[#062136] to-[#1a6b7d] dark:from-slate-900 dark:to-slate-800 border-0 rounded-[20px] text-white"
                style={{ boxShadow: '0 5px 12px rgba(0,0,0,0.12)' }}
                data-testid="balance-summary-card"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-white/70">
                      Fiat balance (USD)
                    </p>
                    <p
                      className="text-3xl md:text-4xl font-bold mt-1"
                      data-testid="fiat-balance-amount"
                    >
                      {formatCurrency(fiatBalance, 'USD')}
                    </p>
                    <Link
                      to="/user/deposit-fiat"
                      className="inline-block mt-2 text-xs text-white/80 hover:text-white underline underline-offset-2"
                    >
                      Deposit fiat
                    </Link>
                  </div>
                  <div className="hidden md:block h-12 w-px bg-white/20" />
                  <div className="md:text-right">
                    <p className="text-xs uppercase tracking-wide text-white/70">
                      Total portfolio value
                    </p>
                    <p
                      className="text-2xl md:text-3xl font-semibold mt-1"
                      data-testid="total-portfolio-amount"
                    >
                      {formatCurrency(totalPortfolioUsd, 'USD')}
                    </p>
                    <p className="text-[11px] text-white/60 mt-1">
                      Crypto valued at live market price
                    </p>
                  </div>
                </div>
              </Card>

              {/* Crypto Overview Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-6">
                {cryptoData.map((crypto, idx) => (
                  <Card
                    key={idx}
                    className="p-6 bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 rounded-[20px]"
                    style={{
                      boxShadow: '0 5px 8px rgba(0,0,0,0.1)',
                    }}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                          style={{
                            backgroundColor: crypto.color,
                            mixBlendMode: 'revert',
                          }}
                        >
                          {crypto.icon}
                        </div>
                        <div className="flex flex-col gap-1">
                          <h3 className="font-bold text-gray-900 dark:text-slate-100">
                            {crypto.name}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-slate-400">
                            {crypto.symbol}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <MiniAreaChart
                        data={miniChartDataBySymbol[crypto.symbol] || []}
                        color={crypto.color}
                        isPositive={crypto.change > 0}
                      />
                    </div>

                    <div className="mt-3">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white truncate">
                        ${crypto.price}
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <span className={`text-xs font-medium flex items-center gap-0.5 flex-shrink-0 ${
                          crypto.change > 0 ? 'text-green-500' : 'text-red-500'
                        }`}>
                          {crypto.change > 0 ? '+' : ''}{crypto.change}%
                        </span>
                        <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                          crypto.change > 0 ? 'bg-green-500' : 'bg-red-500'
                        }`}>
                          {crypto.change > 0
                            ? <ArrowUpRight size={10} className="text-white" />
                            : <ArrowDownRight size={10} className="text-white" />
                          }
                        </span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Transaction History & Main Chart Row */}
              <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Transaction History */}
                <Card
                  className="p-4 md:p-5 col-span-1 bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 rounded-[20px] overflow-hidden min-w-0"
                  style={{
                    boxShadow: '0 5px 8px rgba(0,0,0,0.1)',
                  }}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
                    <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-slate-100">
                      Transaction
                    </h2>
                    <div className="relative">
                      <button
                        className="flex items-center space-x-2 px-3 py-1.5 rounded-lg self-start sm:self-auto bg-gray-100 dark:bg-slate-800 border border-transparent dark:border-slate-700"
                        onClick={() => setFilterOpen((v) => !v)}
                      >
                        <span className="text-sm flex gap-2 items-center text-gray-500 dark:text-slate-300">
                          <Filter className="w-4 h-4" />
                          Filter
                        </span>
                      </button>
                      {filterOpen && (
                        <div className="absolute right-0 mt-2 z-20 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg p-4 min-w-[180px]">
                          <div className="mb-3">
                            <label className="block text-xs font-semibold mb-1 text-gray-500 dark:text-slate-300">Type</label>
                            <select
                              className="w-full p-1 rounded border border-gray-200 dark:border-slate-700 text-sm bg-white dark:bg-slate-950 text-gray-900 dark:text-slate-100"
                              value={filterType}
                              onChange={e => setFilterType(e.target.value)}
                            >
                              <option value="All">All</option>
                              <option value="Deposit">Deposit</option>
                              <option value="Withdrawal">Withdrawal</option>
                              <option value="Buy">Buy</option>
                              <option value="Sell">Sell</option>
                              <option value="Trade">Trade</option>
                            </select>
                          </div>
                          <div className="mb-2">
                            <label className="block text-xs font-semibold mb-1 text-gray-500 dark:text-slate-300">Status</label>
                            <select
                              className="w-full p-1 rounded border border-gray-200 dark:border-slate-700 text-sm bg-white dark:bg-slate-950 text-gray-900 dark:text-slate-100"
                              value={filterStatus}
                              onChange={e => setFilterStatus(e.target.value)}
                            >
                              <option value="All">All</option>
                              <option value="Pending">Pending</option>
                              <option value="Completed">Completed</option>
                              <option value="Error">Error</option>
                              <option value="Requested">Requested</option>
                              <option value="Processing">Processing</option>
                            </select>
                          </div>
                          <div className="flex justify-end gap-2 mt-2">
                            <button
                              className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-200"
                              onClick={() => {
                                setFilterType('All');
                                setFilterStatus('All');
                              }}
                            >
                              Reset
                            </button>
                            <button
                              className={`text-xs px-2 py-1 rounded text-white ${
                                theme === 'dark' ? 'bg-blue-600' : 'bg-[#062136]'
                              }`}
                              onClick={() => setFilterOpen(false)}
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-0 mb-4">
                    {/* Column header */}
                    <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-slate-800 px-1">
                      <div className="w-7 flex-shrink-0" />
                      <span className="flex-1 text-xs font-medium text-gray-500 dark:text-slate-400 min-w-0">Type</span>
                      <span className="w-16 text-xs font-medium text-gray-500 dark:text-slate-400 text-right flex-shrink-0">Amount</span>
                      <span className="w-20 text-xs font-medium text-gray-500 dark:text-slate-400 text-right flex-shrink-0">Status</span>
                    </div>

                    {filteredTransactions.length === 0 && (
                      <p className="text-sm text-gray-400 dark:text-slate-500 text-center py-4">No transactions yet</p>
                    )}
                    {filteredTransactions.slice(0, 6).map((tx) => (
                      <div
                        key={tx.id}
                        className="flex items-center gap-2 py-2.5 border-b border-gray-100 dark:border-slate-800 last:border-0 px-1"
                      >
                        {/* Icon */}
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                          tx.type?.includes('deposit') || tx.type === 'deposit'
                            ? 'bg-green-100 dark:bg-green-900/30'
                            : 'bg-red-100 dark:bg-red-900/30'
                        }`}>
                          {tx.type?.includes('deposit')
                            ? <ArrowDown size={13} className="text-green-600 dark:text-green-400" />
                            : <ArrowUp size={13} className="text-red-500 dark:text-red-400" />
                          }
                        </div>

                        {/* Type + asset — takes remaining space */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white capitalize truncate leading-tight">
                            {tx.type}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-slate-400 truncate leading-tight">
                            {tx.asset_symbol || tx.asset || tx.currency || '—'}
                          </p>
                        </div>

                        {/* Amount — fixed width */}
                        <div className="w-16 flex-shrink-0 text-right">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {tx.amount}
                          </p>
                        </div>

                        {/* Status — fixed width */}
                        <div className="w-20 flex-shrink-0 flex justify-end">
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${
                            tx.status === 'completed' || tx.status === 'approved'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : tx.status === 'pending' || tx.status === 'processing'
                              ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                          }`}>
                            {tx.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Main Price Chart */}
                <Card
                  className="p-4 md:p-5 col-span-1 lg:col-span-2 bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 rounded-[20px] min-w-0 overflow-hidden"
                  style={{
                    boxShadow: '0 5px 8px rgba(0,0,0,0.1)',
                  }}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
                    <div className="flex items-center space-x-1 sm:space-x-2 bg-gray-200 dark:bg-slate-800 rounded-lg p-1 overflow-x-auto">
                      {['day', 'week', 'month', 'year'].map((period) => (
                        <button
                          key={period}
                          onClick={() => setChartPeriod(period)}
                          className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${chartPeriod === period
                            ? theme === 'dark'
                              ? 'text-white bg-blue-600'
                              : 'text-white bg-[#062136]'
                            : 'text-gray-500 dark:text-slate-400 bg-transparent'
                            }`}
                        >
                          {period.charAt(0).toUpperCase() + period.slice(1)}
                        </button>
                      ))}
                    </div>
                    <Select
                      value={selectedChartAsset}
                      onValueChange={setSelectedChartAsset}
                    >
                      <SelectTrigger className="w-auto shrink-0 justify-start gap-2 px-3 py-1.5 h-auto rounded-[20px] self-start sm:self-auto border-none shadow-none focus:ring-0 bg-transparent">
                        <div
                          className="w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                          style={{
                            backgroundColor:
                              assetColorMap[selectedChartAsset] || '#ffe1c7',
                          }}
                        >
                          {assetIconMap[selectedChartAsset] || selectedChartAsset}
                        </div>
                        <span className="text-sm font-medium whitespace-nowrap text-gray-900 dark:text-slate-100">
                          {selectedChartAsset}
                        </span>
                        {/* <ChevronDown className="w-4 h-4 flex-shrink-0 text-gray-500" /> */}
                      </SelectTrigger>
                      <SelectContent>
                        {(chartSelectableAssets.length > 0
                          ? chartSelectableAssets
                          : ['BTC']
                        ).map((symbol) => (
                          <SelectItem key={symbol} value={symbol}>
                            {symbol}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="relative w-full" style={{ height: '250px', minHeight: '200px' }}>
                    {chartLoading && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 dark:bg-slate-950/70">
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-300">
                          <div className="w-5 h-5 rounded-full border-2 border-gray-300 border-t-[#062136] animate-spin" />
                          Loading chart...
                        </div>
                      </div>
                    )}
                    <div
                      ref={chartContainerRef}
                      className="w-full overflow-hidden"
                      style={{ height: '250px', minHeight: '200px' }}
                    />
                  </div>
                </Card>
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="col-span-1 xl:col-span-4 space-y-6 p-4 md:p-6 xl:p-6 border-t xl:border-t-0 border-gray-200 dark:border-slate-800 overflow-x-hidden">
              {/* User Profile Card */}
              <Card
                className="shadow-none bg-transparent border-none gap-4"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div className="hidden lg:flex">
                  <span
                    className="px-2 py-0.5 rounded-full text-[11px] font-semibold text-center whitespace-nowrap align-middle"
                    style={{
                      backgroundColor: theme === 'dark' ? 'rgba(33, 188, 254, 0.1)' : 'rgba(6, 33, 54, 0.1)',
                      color: theme === 'dark' ? '#21bcfe' : '#062136',
                      border: `1px solid ${theme === 'dark' ? 'rgba(33, 188, 254, 0.2)' : 'rgba(6, 33, 54, 0.2)'}`,
                    }}
                  >
                    Premium account
                  </span>
                </div>
                <Link
                  to="/user/settings"
                  className="hidden lg:flex items-center space-x-2 sm:space-x-3 cursor-pointer"
                  aria-label="Go to settings"
                >
                  <div>
                    <p
                      className="font-bold text-right text-sm sm:text-base text-gray-900 dark:text-slate-100"
                    >
                      {user?.full_name}
                    </p>
                    {/* <p className="text-xs" style={{ color: '#7B8591' }}>
                      {user?.email}
                    </p> */}
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-200 items-center justify-center overflow-hidden flex-shrink-0">
                    {user?.avatar_url ? (
                      <img
                        src={user.avatar_url.startsWith('http') ? user.avatar_url : `${process.env.REACT_APP_BACKEND_URL || ''}${user.avatar_url}`}
                        alt="Profile"
                        className="w-full h-full object-cover rounded-full"
                        onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div
                        className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center text-white font-bold rounded-full"
                        style={{
                          background:
                            theme === 'dark'
                              ? 'linear-gradient(to bottom right, #60a5fa, #2563eb)'
                              : 'linear-gradient(to bottom right, #0b2f4d, #062136)',
                        }}
                      >
                        {user?.full_name?.charAt(0)}
                      </div>
                    )}
                  </div>
                </Link>
              </Card>
              {/* Exchange Widget */}
              <Card
                className="p-4 md:p-6 bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 rounded-[20px] overflow-hidden min-w-0"
                style={{
                  boxShadow: '2px 10px 10px rgba(0,0,0,0.1)',
                }}
              >
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                  <p className="text-xs text-blue-700 dark:text-blue-400 font-medium">
                    How to exchange: Select the asset you have (From), select what you want (To), enter amount and click Exchange Now
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
                  <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-slate-100">
                    Exchange
                  </h2>

                  <div className="relative flex rounded-xl border-none w-full sm:w-[150px] bg-gray-100 dark:bg-slate-800">
                    {/* Animated sliding background */}
                    <div
                      className="absolute top-1 bottom-1 rounded-xl transition-all duration-300 ease-in-out bg-white dark:bg-slate-900"
                      style={{
                        left: exchangeMode === 'buy' ? '4px' : '50%',
                        width: 'calc(50% - 4px)',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                      }}
                    />

                    {/* Radio buttons */}
                    {/* <button
                      onClick={() => setExchangeMode('buy')}
                      className="relative flex-1 py-2.5 text-sm font-medium transition-colors duration-300 z-10 flex items-center justify-center"
                    >
                      Buy
                    </button>

                    <button
                      onClick={() => setExchangeMode('sell')}
                      className="relative flex-1 py-2.5 text-sm font-medium transition-colors duration-300 z-10 flex items-center justify-center"
                    >
                      Sell
                    </button> */}
                  </div>
                </div>

                <div className="space-y-2">
                  <div>
                    <label
                      className="text-sm font-medium mb-2 block text-gray-500 dark:text-slate-300"
                    >
                      From
                    </label>
                    <div
                      className="flex items-center space-x-2 p-3 rounded-lg border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900 overflow-hidden"
                    >
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                        style={{ backgroundColor: assetColorMap[exchangeData.from] || '#ffe1c7' }}
                      >
                        {assetIconMap[exchangeData.from] || exchangeData.from}
                      </div>
                      <div className="flex-1">
                        <Select
                          value={exchangeData.from}
                          onValueChange={value => setExchangeData(prev => ({ ...prev, from: value }))}
                        >
                          <SelectTrigger className="border-0 p-0 h-auto w-auto focus:ring-0">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {cryptoAssets.map((asset) => (
                              <SelectItem key={asset.asset_symbol} value={asset.asset_symbol}>
                                 ({asset.asset_symbol})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formatNumberWithCommas(exchangeData.fromAmount)}
                        onChange={e => setExchangeData(prev => ({ ...prev, fromAmount: sanitizeNumericInput(e.target.value) }))}
                        className="text-sm font-medium ml-2 whitespace-nowrap flex-shrink-0 text-gray-900 dark:text-slate-100 bg-transparent border-none outline-none w-24 text-right"
                        placeholder="0.00"
                      />
                      <button
                        type="button"
                        className={`ml-2 text-xs font-medium ${
                          theme === 'dark'
                            ? 'text-blue-600 hover:text-blue-700'
                            : 'text-[#062136] hover:text-[#0b2f4d]'
                        }`}
                        onClick={() => {
                          setExchangeData(prev => ({
                            ...prev,
                            fromAmount: getWalletBalanceBySymbol(exchangeData.from).toString(),
                          }));
                        }}
                      >
                        MAX
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 ml-1 dark:text-slate-400">
                      Balance: {getWalletBalanceBySymbol(exchangeData.from).toFixed(4)} {exchangeData.from}
                    </p>
                  </div>

                  <div className="flex justify-center">
                    <button
                      type="button"
                      className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 dark:bg-slate-800"
                      onClick={() => {
                        setExchangeData((prev) => ({
                          ...prev,
                          from: prev.to,
                          to: prev.from,
                          fromAmount: '0',
                          toAmount: '0',
                          exchangeRate: null,
                        }));
                      }}
                    >
                      <ArrowUpDown
                        className="w-5 h-5 text-gray-500 dark:text-slate-300"
                      />
                    </button>
                  </div>

                  <div>
                    <label
                      className="text-sm font-medium mb-2 block text-gray-500 dark:text-slate-300"
                    >
                      To
                    </label>
                    <div
                      className="flex items-center space-x-2 p-3 rounded-lg border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900 min-w-0 overflow-hidden"
                    >
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                        style={{ backgroundColor: assetColorMap[exchangeData.to] || '#c7e6ff' }}
                      >
                        {assetIconMap[exchangeData.to] || exchangeData.to}
                      </div>
                      <div className="flex-1 min-w-0">
                        <Select
                          value={exchangeData.to}
                          onValueChange={value => setExchangeData(prev => ({ ...prev, to: value }))}
                        >
                          <SelectTrigger className="border-0 p-0 h-auto w-auto focus:ring-0">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {toAssetOptions.map((asset) => (
                              <SelectItem key={asset.asset_symbol} value={asset.asset_symbol}>
                                {asset.asset_symbol}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <span className="text-sm font-medium ml-2 whitespace-nowrap flex-shrink-0 text-gray-900 dark:text-slate-100 truncate">
                        {Number(exchangeData.toAmount || 0).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  </div>

                  <div className="text-sm text-gray-500 dark:text-slate-400">
                    {exchangeData.exchangeRate
                      ? `1 ${exchangeData.from} = ${exchangeData.exchangeRate.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })} ${exchangeData.to}`
                      : `1 ${exchangeData.from} = -- ${exchangeData.to}`}
                  </div>

                  <Button
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3.5 rounded-xl text-base transition-colors mt-4"
                    onClick={handleDashboardExchange}
                  >
                    Exchange Now →
                  </Button>
                </div>
              </Card>

              {/* Wallets List */}
              <Card
                className="p-4 md:p-6 bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 rounded-[20px] overflow-hidden"
                style={{
                  boxShadow: '2px 2px 10px rgba(0,0,0,0.1)',
                }}
              >
                <h2 className="text-lg md:text-xl font-bold mb-4 text-gray-900 dark:text-slate-100">
                  Assets
                </h2>
                <div className="space-y-3 max-h-48 xl:max-h-none overflow-y-auto">
                  {wallets
                    .filter(w => (w.available_balance || 0) > 0)
                    .sort((a, b) => (b.available_balance || 0) - (a.available_balance || 0))
                    .slice(0, 3)
                    .map((wallet) => {
                      const badge = assetBadgeConfig[wallet.asset_symbol] || {
                        icon: (wallet.asset_symbol || '?').slice(0, 1),
                        color: '#475569',
                        bgClass: 'bg-slate-100',
                      };

                      return (
                        <div
                          key={wallet.id}
                          className="flex items-center justify-between py-2 min-w-0"
                        >
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div
                              className={`w-10 h-10 rounded-full ${badge.bgClass} flex items-center justify-center shadow-sm flex-shrink-0`}
                              style={{ color: badge.color }}
                            >
                              <span className="text-lg font-bold leading-none">
                                {badge.icon}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">
                                {wallet.asset_name || wallet.asset_symbol}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-slate-400">
                                {wallet.asset_symbol}
                              </p>
                            </div>
                          </div>
                          <p className="text-sm font-medium flex-shrink-0 text-gray-900 dark:text-white ml-2">
                            {(wallet.available_balance || 0) > 0
                              ? Number(wallet.available_balance).toFixed(4)
                              : '—'}
                          </p>
                        </div>
                      );
                    })}
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardNew;
